<template>
  <div class="admin-elements">
    <div class="page-header">
      <h2>{{ $t('legacy.Управление Элементами') }}</h2>
      <div class="filters">
        <select v-model="selectedCategoryId" @change="loadSubcategories" class="filter-select">
          <option value="">{{ $t('legacy.Выберите категорию') }}</option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">
            {{ cat.name }}
          </option>
        </select>
        
        <select v-model="selectedSubcategoryId" @change="onSubcategoryChange" class="filter-select" :disabled="!selectedCategoryId">
          <option value="">{{ $t('legacy.Выберите подкатегорию') }}</option>
          <option v-for="sub in subcategories" :key="sub.id" :value="sub.id">
            {{ sub.name }} {{ sub.children_count > 0 ? `(${sub.children_count})` : '' }}
          </option>
        </select>

        <select 
          v-if="childSubcategories.length > 0" 
          v-model="selectedChildSubcategoryId" 
          class="filter-select"
        >
          <option value="">{{ $t('legacy.Выберите под-подкатегорию') }}</option>
          <option v-for="child in childSubcategories" :key="child.id" :value="child.id">
            {{ child.name }}
          </option>
        </select>

        <button 
          @click="showUploadModal = true" 
          class="btn-primary"
          :disabled="!canUpload"
        >
          + {{ $t('legacy.Загрузить Элементы') }}
        </button>
      </div>
    </div>

    <div v-if="elements.length > 0" class="elements-grid">
      <div v-for="element in elements" :key="element.id" class="element-card">
        <div class="element-badge" :class="element.is_free ? 'badge-free' : 'badge-paid'">
          {{ element.is_free ? $t('legacy.Бесплатно') : $t('legacy.Платно') }}
        </div>
        <img :src="element.image_url" :alt="element.name" class="element-image" />
        <div class="element-info">
          <h4>{{ element.name }}</h4>
          <p>{{ $t('legacy.Цена') }}: {{ element.price }} ₽</p>
          <p>{{ $t('legacy.Порядок') }}: {{ element.sort_order }}</p>
        </div>
        <div class="element-actions">
          <button @click="editElement(element)" class="btn-edit">{{ $t('common.edit') }}</button>
          <button @click="deleteElement(element)" class="btn-delete">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

    <div v-else-if="selectedSubcategoryId && selectedSubcategoryHasChildren && !selectedChildSubcategoryId" class="empty-state">
      <p>{{ $t('legacy.Выберите под-подкатегорию для просмотра элементов') }}</p>
    </div>
    
    <div v-else-if="targetSubcategoryId" class="empty-state">
      <p>{{ $t('legacy.Нет элементов в этой подкатегории') }}</p>
    </div>

    <div v-if="showUploadModal" class="modal-overlay" @click.self="closeUploadModal">
      <div class="modal-content upload-modal">
        <h3>{{ $t('legacy.Загрузить Элементы') }}</h3>
        <div class="dropzone" @drop.prevent="handleDrop" @dragover.prevent>
          <input 
            ref="fileInput"
            type="file" 
            multiple 
            accept="image/png"
            @change="handleFileSelect"
            style="display: none"
          />
          <div v-if="uploadFiles.length === 0" class="dropzone-placeholder">
            <p>{{ $t('legacy.Перетащите PNG файлы сюда или') }}</p>
            <button type="button" @click="$refs.fileInput.click()" class="btn-select">
              {{ $t('legacy.Выбрать файлы') }}
            </button>
          </div>
          <div v-else class="files-preview">
            <div v-for="(file, index) in uploadFiles" :key="index" class="file-item">
              <img :src="file.preview" alt="preview" class="file-preview" />
              <input 
                v-model="file.name" 
                class="file-name-input" 
                :placeholder="$t('legacy.Название элемента')"
              />
              <input 
                v-model.number="file.price" 
                type="number" 
                class="file-price-input" 
                :placeholder="$t('legacy.Цена')"
              />
              <button @click="removeFile(index)" class="btn-remove">×</button>
            </div>
          </div>
        </div>

        <div class="upload-options">
          <label class="checkbox-label">
            <input type="checkbox" v-model="uploadIsFree" />
            <span>{{ $t('legacy.Бесплатные элементы') }}</span>
          </label>
          <p class="hint">{{ $t('legacy.Бесплатные элементы доступны всем пользователям') }}</p>
        </div>

        <div v-if="isUploading" class="upload-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <p class="progress-text">
            {{ $t('legacy.Загрузка') }}: {{ uploadedCount }} {{ $t('legacy.из') }} {{ uploadFiles.length }} ({{ uploadProgress }}%)
          </p>
        </div>

        <div class="form-actions">
          <button @click="closeUploadModal" class="btn-cancel" :disabled="isUploading">
            {{ $t('common.cancel') }}
          </button>
          <button 
            @click="uploadElements" 
            class="btn-primary"
            :disabled="uploadFiles.length === 0 || isUploading"
          >
            <span v-if="isUploading">{{ $t('common.loading') }}</span>
            <span v-else>{{ $t('legacy.Загрузить') }} ({{ uploadFiles.length }})</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="editingElement" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content">
        <h3>{{ $t('legacy.Редактировать Элемент') }}</h3>
        <form @submit.prevent="saveElement">
          <div class="form-group">
            <label>{{ $t('legacy.Название') }}</label>
            <input v-model="editForm.name" type="text" required />
          </div>

          <div class="form-group">
            <label>{{ $t('legacy.Цена') }}</label>
            <input v-model.number="editForm.price" type="number" step="0.01" required />
          </div>

          <div class="form-group">
            <label>{{ $t('legacy.Порядок сортировки') }}</label>
            <input v-model.number="editForm.sort_order" type="number" />
          </div>

          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="editForm.is_free" />
              <span>{{ $t('legacy.Бесплатный элемент') }}</span>
            </label>
            <p class="hint">{{ $t('legacy.Бесплатные элементы доступны всем пользователям') }}</p>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeEditModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary">{{ $t('common.save') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '@/api/admin';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminElements',
  data() {
    return {
      toast: null,
      categories: [],
      subcategories: [],
      childSubcategories: [],
      elements: [],
      selectedCategoryId: '',
      selectedSubcategoryId: '',
      selectedChildSubcategoryId: '',
      showUploadModal: false,
      uploadFiles: [],
      isUploading: false,
      uploadProgress: 0,
      uploadedCount: 0,
      editingElement: null,
      editForm: {
        name: '',
        price: 0,
        sort_order: 0,
        is_free: true
      },
      uploadIsFree: true,
      loadRequestId: 0,
      childLoadRequestId: 0,
      subcategoryLoadRequestId: 0
    }
  },
  computed: {
    selectedSubcategoryHasChildren() {
      const sub = this.subcategories.find(s => s.id == this.selectedSubcategoryId);
      return sub && sub.children_count > 0;
    },
    canUpload() {
      if (this.selectedSubcategoryHasChildren) {
        return !!this.selectedChildSubcategoryId;
      }
      return !!this.selectedSubcategoryId;
    },
    targetSubcategoryId() {
      if (this.selectedSubcategoryHasChildren) {
        return this.selectedChildSubcategoryId || null;
      }
      return this.selectedSubcategoryId || null;
    }
  },
  watch: {
    selectedChildSubcategoryId(newVal) {
      if (newVal) {
        this.loadElementsFromApi(newVal);
      } else if (this.childSubcategories.length > 0) {
        this.loadRequestId++;
        this.elements = [];
      }
    }
  },
  async created() {
    this.toast = useToast();
    await this.loadCategories();
  },
  methods: {
    async loadCategories() {
      try {
        const response = await adminApi.getCategories();
        this.categories = response.data.categories || [];
      } catch (error) {
        this.toast.error(this.$t('legacy.Ошибка загрузки категорий'));
      }
    },

    async loadSubcategories() {
      this.selectedSubcategoryId = '';
      this.selectedChildSubcategoryId = '';
      this.childSubcategories = [];
      this.elements = [];
      this.subcategories = [];
      this.loadRequestId++;
      this.childLoadRequestId++;
      
      if (!this.selectedCategoryId) {
        return;
      }
      
      const requestId = ++this.subcategoryLoadRequestId;
      const currentCategoryId = this.selectedCategoryId;
      
      try {
        const response = await adminApi.getSubcategories(currentCategoryId);
        
        if (requestId !== this.subcategoryLoadRequestId) {
          return;
        }
        
        if (currentCategoryId !== this.selectedCategoryId) {
          return;
        }
        
        this.subcategories = (response.data.subcategories || []).map(s => ({
          ...s,
          children_count: parseInt(s.children_count) || 0
        }));
      } catch (error) {
        if (requestId !== this.subcategoryLoadRequestId) {
          return;
        }
        this.toast.error(this.$t('legacy.Ошибка загрузки подкатегорий'));
        this.subcategories = [];
      }
    },

    async onSubcategoryChange() {
      this.selectedChildSubcategoryId = '';
      this.childSubcategories = [];
      this.elements = [];
      this.loadRequestId++;
      this.childLoadRequestId++;
      
      if (!this.selectedSubcategoryId) return;
      
      const subcategory = this.subcategories.find(s => s.id == this.selectedSubcategoryId);
      const currentSubcategoryId = this.selectedSubcategoryId;
      
      if (subcategory && subcategory.children_count > 0) {
        const childRequestId = ++this.childLoadRequestId;
        try {
          const response = await adminApi.getChildSubcategories(currentSubcategoryId);
          
          if (childRequestId !== this.childLoadRequestId) {
            return;
          }
          
          if (currentSubcategoryId !== this.selectedSubcategoryId) {
            return;
          }
          
          this.childSubcategories = response.data.subcategories || [];
        } catch (error) {
          if (childRequestId !== this.childLoadRequestId) {
            return;
          }
          this.toast.error(this.$t('legacy.Ошибка загрузки под-подкатегорий'));
          this.childSubcategories = [];
        }
      } else {
        await this.loadElementsFromApi(currentSubcategoryId);
      }
    },

    async loadElementsFromApi(subcategoryId) {
      const requestId = ++this.loadRequestId;
      
      try {
        const response = await adminApi.getElements(subcategoryId);
        
        if (requestId !== this.loadRequestId) {
          return;
        }
        
        this.elements = response.data.elements || [];
      } catch (error) {
        if (requestId !== this.loadRequestId) {
          return;
        }
        
        if (error.response?.status === 400) {
          this.elements = [];
        } else if (error.response?.status === 404) {
          this.toast.error(this.$t('legacy.Подкатегория не найдена'));
          this.selectedSubcategoryId = '';
          this.selectedChildSubcategoryId = '';
          this.elements = [];
        } else {
          this.toast.error(this.$t('legacy.Ошибка загрузки элементов'));
          this.elements = [];
        }
      }
    },

    handleFileSelect(event) {
      this.processFiles(Array.from(event.target.files));
    },

    handleDrop(event) {
      this.processFiles(Array.from(event.dataTransfer.files));
    },

    processFiles(files) {
      files.forEach(file => {
        if (file.type === 'image/png') {
          const reader = new FileReader();
          reader.onload = (e) => {
            const nameWithoutExt = file.name.replace('.png', '').replace(/_/g, ' ');
            const formattedName = nameWithoutExt.split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');

            this.uploadFiles.push({
              file,
              name: formattedName,
              price: 0,
              preview: e.target.result
            });
          };
          reader.readAsDataURL(file);
        }
      });
    },

    removeFile(index) {
      this.uploadFiles.splice(index, 1);
    },

    async uploadElements() {
      const uploadTargetId = this.targetSubcategoryId;
      if (!uploadTargetId) {
        this.toast.error(this.$t('legacy.Выберите подкатегорию для загрузки'));
        return;
      }
      
      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadedCount = 0;
      const totalFiles = this.uploadFiles.length;

      try {
        for (let i = 0; i < this.uploadFiles.length; i++) {
          const fileData = this.uploadFiles[i];
          const formData = new FormData();
          formData.append('subcategory_id', uploadTargetId);
          formData.append('name', fileData.name);
          formData.append('price', fileData.price);
          formData.append('is_free', this.uploadIsFree);
          formData.append('image', fileData.file);

          await adminApi.createElement(formData);
          
          this.uploadedCount++;
          this.uploadProgress = Math.round((this.uploadedCount / totalFiles) * 100);
        }

        this.toast.success(this.$t('legacy.dynamic.uploadedElements', { count: totalFiles }));
        if (uploadTargetId === this.targetSubcategoryId) {
          await this.loadElementsFromApi(uploadTargetId);
        }
        this.closeUploadModal();
      } catch (error) {
        this.toast.error(this.$t('legacy.dynamic.uploadElementsError', {
          uploaded: this.uploadedCount,
          total: totalFiles
        }));
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadedCount = 0;
      }
    },

    editElement(element) {
      this.editingElement = element;
      this.editForm = {
        name: element.name,
        price: element.price,
        sort_order: element.sort_order,
        is_free: element.is_free !== false
      };
    },

    async saveElement() {
      const elementSubcategoryId = this.editingElement.subcategory_id;
      try {
        await adminApi.updateElement(this.editingElement.id, this.editForm);
        this.toast.success(this.$t('legacy.Элемент обновлен'));
        if (elementSubcategoryId === this.targetSubcategoryId) {
          await this.loadElementsFromApi(elementSubcategoryId);
        }
        this.closeEditModal();
      } catch (error) {
        this.toast.error(this.$t('errors.save'));
      }
    },

    async deleteElement(element) {
       if (!confirm(this.$t('legacy.dynamic.elementDelete', { name: element.name }))) return;
      const elementSubcategoryId = element.subcategory_id;

      try {
        await adminApi.deleteElement(element.id);
        this.toast.success(this.$t('legacy.Элемент удален'));
        if (elementSubcategoryId === this.targetSubcategoryId) {
          await this.loadElementsFromApi(elementSubcategoryId);
        }
      } catch (error) {
        this.toast.error(this.$t('errors.delete'));
      }
    },

    closeUploadModal() {
      this.showUploadModal = false;
      this.uploadFiles = [];
    },

    closeEditModal() {
      this.editingElement = null;
      this.editForm = { name: '', price: 0, sort_order: 0 };
    }
  }
}
</script>

<style scoped lang="scss">
.admin-elements {
  .page-header {
    margin-bottom: 30px;

    h2 {
      margin: 0 0 20px 0;
    }

    .filters {
      display: flex;
      gap: 15px;
      align-items: center;

      .filter-select {
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        min-width: 200px;

        &:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }
      }
    }
  }

  .btn-primary {
    background: #007bff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: #0056b3;
    }
  }

  .elements-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
  }

  .element-card {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    position: relative;

    .element-badge {
      position: absolute;
      top: 8px;
      right: 8px;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      z-index: 1;
      
      &.badge-free {
        background: #10b981;
        color: white;
      }
      
      &.badge-paid {
        background: #f59e0b;
        color: white;
      }
    }

    .element-image {
      width: 100%;
      height: 200px;
      object-fit: contain;
      background: #f5f5f5;
    }

    .element-info {
      padding: 15px;

      h4 {
        margin: 0 0 8px 0;
        font-size: 16px;
      }

      p {
        margin: 4px 0;
        color: #666;
        font-size: 14px;
      }
    }

    .element-actions {
      padding: 0 15px 15px 15px;
      display: flex;
      gap: 10px;

      button {
        flex: 1;
        padding: 8px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;

        &.btn-edit {
          background: #28a745;
          color: white;

          &:hover {
            background: #218838;
          }
        }

        &.btn-delete {
          background: #dc3545;
          color: white;

          &:hover {
            background: #c82333;
          }
        }
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #999;
    font-size: 16px;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    padding: 30px;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;

    &.upload-modal {
      max-width: 800px;
    }

    h3 {
      margin-top: 0;
    }

    .dropzone {
      border: 2px dashed #ddd;
      border-radius: 8px;
      padding: 30px;
      text-align: center;
      min-height: 200px;

      .dropzone-placeholder {
        p {
          margin: 0 0 15px 0;
          color: #666;
        }

        .btn-select {
          padding: 10px 24px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;

          &:hover {
            background: #0056b3;
          }
        }
      }

      .files-preview {
        display: grid;
        gap: 15px;
        max-height: 400px;
        overflow-y: auto;

        .file-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 4px;

          .file-preview {
            width: 60px;
            height: 60px;
            object-fit: contain;
            background: white;
            border-radius: 4px;
          }

          .file-name-input, .file-price-input {
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
          }

          .file-name-input {
            flex: 2;
          }

          .file-price-input {
            flex: 1;
          }

          .btn-remove {
            width: 30px;
            height: 30px;
            background: #dc3545;
            color: white;
            border: none;
            border-radius: 50%;
            cursor: pointer;
            font-size: 20px;
            line-height: 1;

            &:hover {
              background: #c82333;
            }
          }
        }
      }
    }

    .upload-progress {
      margin: 20px 0;
      
      .progress-bar {
        width: 100%;
        height: 24px;
        background: #e9ecef;
        border-radius: 4px;
        overflow: hidden;
        
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #007bff, #0056b3);
          transition: width 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 12px;
          font-weight: 500;
        }
      }
      
      .progress-text {
        margin-top: 8px;
        text-align: center;
        font-size: 14px;
        color: #666;
        font-weight: 500;
      }
    }

    .form-group {
      margin-bottom: 20px;

      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
      }

      input {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
      }
      
      &.checkbox-group {
        input[type="checkbox"] {
          width: auto;
          margin-right: 8px;
        }
      }
    }
    
    .checkbox-label {
      display: flex;
      align-items: center;
      cursor: pointer;
      
      input[type="checkbox"] {
        width: 18px;
        height: 18px;
        margin-right: 10px;
        cursor: pointer;
      }
      
      span {
        font-weight: 500;
      }
    }
    
    .hint {
      font-size: 12px;
      color: #666;
      margin-top: 6px;
    }
    
    .upload-options {
      padding: 15px;
      background: #f9fafb;
      border-radius: 8px;
      margin-bottom: 15px;
    }

    .form-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 20px;

      button {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;

        &.btn-cancel {
          background: #6c757d;
          color: white;

          &:hover {
            background: #5a6268;
          }
        }
      }
    }
  }
}
</style>
